import React, { useState, useContext } from 'react';
import { authContext } from '../../context/AuthContext.js';
import {
  Box, Button, Checkbox, CircularProgress, FormControlLabel, IconButton, Stack, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DynamicEntry from './DynamicEntry';
import ManualEntryHeader from './ManualEntryHeader';
import PostInvoice from '../../services/PostInvoice';
import SnackbarEx from '../Helpers/SnackbarEx';
import { currencyFormatter } from '../Helpers/CurrencyFormat';
import { tokens } from '../../theme';

export default function ManualEntry({ close, newIngredients, invoiceOrigin, invoiceOriginVendor, uploadEdit }) {
  const { userRole } = useContext(authContext);
  const isEdit = Boolean(uploadEdit && invoiceOrigin);
  const isReInvoice = Boolean(invoiceOrigin) && !isEdit;
  const [autoManualCheck, setAutoManualCheck] = useState(false);
  const [invoiceSum, setInvoiceSum] = useState(0);
  const [submitLoad, setSubmitLoad] = useState(false);
  const [openTestSnack, setOpenTestSnack] = useState({
    color: '',
    message: '',
    open: false,
  });

  const [invoiceHeader, setInvoiceHeader] = useState([{
    vendor: '',
    vendorId: null,
    street: '',
    city: '',
    zip: '',
    state: '',
    invoiceNum: '',
    date: '',
    _id: null,
  }]);

  const [invoiceBodyData, setInvoiceBodyData] = useState([{
    lineType: 'ingredient',
    ingredient: '',
    ingredientId: '',
    itemId: null,
    category: null,
    vendorItemId: null,
    recipeUoM: '',
    itemUoM: '',
    itemSize: null,
    pack: '',
    packQuantity: null,
    packUnitQuantity: null,
    recipeIngQuantity: null,
    quantity: null,
    total: null,
  }]);

  const [addedCosts, setAddedCosts] = useState({
    discount: 0.0,
    tax: 0.0,
    shipping: 0.0,
  });

  const handleClose = () => close('exitIcon');
  const handleAmountChange = (sum) => setInvoiceSum(sum);
  const updateHeader = (value) => setInvoiceHeader(value);

  const updateBody = (value, index, del, obj) => {
    if (del) {
      setInvoiceBodyData(value);
    } else if (obj === 'addedCost') {
      setAddedCosts((cur) => ({ ...cur, [index]: Number(value) }));
    } else {
      const next = [...invoiceBodyData];
      next[index] = value[index];
      setInvoiceBodyData(next);
    }
  };

  const validateForm = () => {
    const allBodyFilled = invoiceBodyData.every((value) => {
      const isProduct = value.lineType === 'product';
      const hasCommon = Boolean(
        value.ingredient
        && value.pack
        && value.packQuantity
        && value.packUnitQuantity
        && value.total !== null
        && value.total >= 0,
      );
      if (!hasCommon) return false;
      if (isProduct) {
        return value.itemId != null && value.itemId !== '';
      }
      return Boolean(
        value.ingredientId != null
        && value.ingredientId !== ''
        && value.recipeUoM
        && value.itemUoM
        && value.itemSize
        && value.quantity,
      );
    });
    // Edit: vendor, invoice #, and date required (address optional)
    const allHeaderFilled = invoiceHeader.every((value) => {
      if (isEdit) return Boolean(value.vendor && value.invoiceNum && value.date);
      return value.vendor && value.street && value.city
        && value.zip && value.state && value.invoiceNum && value.date;
    });
    return allBodyFilled && allHeaderFilled;
  };

  const postToApi = async () => {
    if (!validateForm()) {
      setOpenTestSnack({
        color: 'error',
        message: 'Fill all fields and match each ingredient line to existing inventory.',
        open: true,
      });
      return;
    }
    const autoMan = !autoManualCheck ? 'manual' : 'auto';
    const mode = isEdit ? 'edit' : (uploadEdit ? 'pdfUpload' : autoMan);
    setSubmitLoad(true);
    const headerPayload = invoiceHeader.map((h) => ({
      ...h,
      _id: isEdit ? (h._id || invoiceOrigin) : undefined,
    }));
    const { success, error, retIngredients } = await PostInvoice(
      {
        invHeader: headerPayload,
        invBody: invoiceBodyData,
        invoiceTotal: invoiceSum,
        addedCosts,
      },
      `api/invoice/${mode}`,
      false,
    );
    setSubmitLoad(false);
    if (success) {
      close('success');
      if (retIngredients?.length > 0 && typeof newIngredients === 'function') {
        newIngredients(retIngredients);
      }
    } else if (error) {
      const errMessage = error?.response?.data || 'Unable to save invoice.';
      setOpenTestSnack({ color: 'error', message: errMessage, open: true });
    }
  };

  return (
    <Box
      sx={{
        height: '100%',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: tokens.bg,
      }}
    >
      <Box
        sx={{
          px: { xs: 2, md: 3 },
          py: 2,
          backgroundColor: tokens.surface,
          borderBottom: `1px solid ${tokens.border}`,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: 11,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: tokens.accent,
              fontWeight: 700,
              mb: 0.5,
            }}
          >
            {isEdit ? 'Edit invoice' : isReInvoice ? 'Re-invoice' : 'Purchases'}
          </Typography>
          <Typography
            sx={{
              fontFamily: "'Fraunces', 'Figtree', serif",
              fontWeight: 650,
              fontSize: { xs: 24, md: 28 },
              letterSpacing: '-0.02em',
              color: tokens.ink,
              lineHeight: 1.15,
            }}
          >
            {isEdit ? 'Update invoice' : isReInvoice ? 'Repeat invoice' : 'Add invoice'}
          </Typography>
          <Typography variant="body2" sx={{ color: tokens.inkMuted, mt: 0.5 }}>
            {isEdit
              ? 'Change line items, discount, tax, or shipping, then save.'
              : isReInvoice
                ? 'Review vendor details and line items, then submit as a new invoice.'
                : 'Enter vendor, invoice details, and line items to update inventory.'}
          </Typography>
        </Box>
        <IconButton onClick={handleClose} aria-label="Close" sx={{ color: tokens.inkMuted }}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          px: { xs: 2, md: 3 },
          py: 2.5,
          pb: 14,
        }}
      >
        <Box
          sx={{
            maxWidth: 1100,
            mx: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <Box
            sx={{
              backgroundColor: tokens.surface,
              border: `1px solid ${tokens.border}`,
              borderRadius: 2,
              p: { xs: 2, md: 2.5 },
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
              Vendor & invoice
            </Typography>
            <Typography variant="body2" sx={{ color: tokens.inkMuted, mb: 2 }}>
              Who you purchased from and the invoice identity.
            </Typography>
            <ManualEntryHeader
              updateHeader={updateHeader}
              invoiceOrigin={invoiceOrigin}
              invOriginVendor={invoiceOriginVendor}
              uploadEdit={isEdit || uploadEdit}
              addressOptional={isEdit}
            />
          </Box>

          <Box
            sx={{
              backgroundColor: tokens.surface,
              border: `1px solid ${tokens.border}`,
              borderRadius: 2,
              p: { xs: 2, md: 2.5 },
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
              Line items
            </Typography>
            <Typography variant="body2" sx={{ color: tokens.inkMuted, mb: 2 }}>
              Mark each line as an ingredient or product. Ingredients update raw stock;
              products increase for-sale count by the units purchased.
            </Typography>
            <DynamicEntry
              invoiceSum={handleAmountChange}
              updateBody={updateBody}
              invoiceOrigin={invoiceOrigin}
              invOriginVendor={invoiceOriginVendor}
              selectedVendor={invoiceHeader[0].vendorId}
            />
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          position: 'sticky',
          bottom: 0,
          px: { xs: 2, md: 3 },
          py: 1.75,
          backgroundColor: tokens.surface,
          borderTop: `1px solid ${tokens.border}`,
          boxShadow: '0 -8px 24px rgba(44, 36, 27, 0.06)',
        }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          alignItems={{ sm: 'center' }}
          justifyContent="space-between"
          sx={{ maxWidth: 1100, mx: 'auto' }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: 11,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                color: tokens.inkMuted,
                fontWeight: 650,
              }}
            >
              Invoice total
            </Typography>
            <Typography
              sx={{
                fontFamily: "'Fraunces', 'Figtree', serif",
                fontWeight: 650,
                fontSize: 28,
                color: tokens.accent,
                lineHeight: 1.1,
              }}
            >
              {currencyFormatter(invoiceSum || 0, 2)}
            </Typography>
          </Box>

          <Stack direction="row" spacing={1.25} alignItems="center" flexWrap="wrap" useFlexGap>
            {userRole === 'AppAdmin' && !isEdit && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={autoManualCheck}
                    onChange={() => setAutoManualCheck(!autoManualCheck)}
                    size="small"
                  />
                }
                label="Auto process"
                sx={{ mr: 1, color: tokens.inkMuted }}
              />
            )}
            <Button variant="outlined" onClick={handleClose} disabled={submitLoad}>
              Cancel
            </Button>
            <Button variant="contained" onClick={postToApi} disabled={submitLoad} sx={{ minWidth: 140 }}>
              {submitLoad
                ? <CircularProgress size={18} sx={{ color: '#fff' }} />
                : (isEdit ? 'Save changes' : 'Submit invoice')}
            </Button>
          </Stack>
        </Stack>
      </Box>

      {openTestSnack.open && (
        <SnackbarEx
          color={openTestSnack.color}
          message={openTestSnack.message}
          open={openTestSnack.open}
          close={() => setOpenTestSnack({ color: '', message: '', open: false })}
        />
      )}
    </Box>
  );
}
